/**
 * Countdown Timer Component
 *
 * Visual countdown timer for quiz questions with animations.
 */

import { useEffect, useState } from 'react';

interface CountdownTimerProps {
  seconds: number;
  totalSeconds: number;
  size?: 'sm' | 'md' | 'lg';
  showProgress?: boolean;
  onComplete?: () => void;
}

export function CountdownTimer({
  seconds,
  totalSeconds,
  size = 'lg',
  showProgress = true,
  onComplete,
}: CountdownTimerProps) {
  const [isLow, setIsLow] = useState(false);
  const [isCritical, setIsCritical] = useState(false);

  useEffect(() => {
    setIsLow(seconds <= 10);
    setIsCritical(seconds <= 5);

    if (seconds === 0 && onComplete) {
      onComplete();
    }
  }, [seconds, onComplete]);

  const progress = totalSeconds > 0 ? (seconds / totalSeconds) * 100 : 0;

  const sizeClasses = {
    sm: { circle: 'w-16 h-16', text: 'text-xl' },
    md: { circle: 'w-24 h-24', text: 'text-3xl' },
    lg: { circle: 'w-32 h-32', text: 'text-5xl' },
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const colorClass = isCritical
    ? 'text-red-500'
    : isLow
    ? 'text-yellow-500'
    : 'text-white';

  const strokeColor = isCritical
    ? '#ef4444'
    : isLow
    ? '#eab308'
    : '#a855f7';

  return (
    <div className={`relative ${sizeClasses[size].circle} flex items-center justify-center`}>
      {showProgress && (
        <svg
          className="absolute inset-0 -rotate-90 transform"
          viewBox="0 0 100 100"
        >
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={strokeColor}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-linear"
          />
        </svg>
      )}

      <span
        className={`font-bold ${sizeClasses[size].text} ${colorClass} ${
          isCritical ? 'animate-pulse' : ''
        }`}
      >
        {seconds}
      </span>
    </div>
  );
}

export default CountdownTimer;
